<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import FormDropdownCurtain from './FormDropdownCurtain.vue';
import {
  FormDropdownEmits,
  FormDropdownOption,
  FormDropdownProps,
  FormDropdownRef,
} from '@/types/form/form-dropdown/form-dropdown';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import utils from '@/utils';
import services from '@/services/index';
import { ModalRef } from '@/types/elements/modal';
import useDevice from '@/hooks/useDevice';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';

const props = defineProps<FormDropdownProps>();
const emit = defineEmits<FormDropdownEmits>();
const { isMobileOrTablet } = useDevice();
const formContext = inject<DynamicFormContext>('formContext')!;
const FIELD_TYPE = 'form-dropdown';
const rootEl = ref<HTMLDivElement | null>(null);
const mobileModalRef = ref<ModalRef>();
const desktopCurtainRef = ref<InstanceType<typeof FormDropdownCurtain>>();
const mobileCurtainRef = ref<InstanceType<typeof FormDropdownCurtain>>();
const focused = ref(false);
const selectedOption = ref<FormDropdownOption | undefined | null>(null);
const parsedOptions = ref<FormDropdownOption[]>([]);
const msg = ref<FormElMsgData>();
const loading = ref(false);

const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const blurTimeout = ref<any>(null);

onBeforeUnmount(() => {
  if (blurTimeout.value) clearTimeout(blurTimeout.value);
});

const fields = computed(() => props.item?.fields);
const label = computed(() => fields.value?.label?.value || '');
const showLabel = computed(() => {
  const text = label.value.replace(/<[^>]*>?/gm, '').trim();
  return text.length > 0;
});
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const clearable = computed(() => fields.value?.clearable?.value ?? false);
const clearText = computed(() => fields.value?.clearText?.value || placeholder.value || 'Clear');
const sourceType = computed(() => utils.sp.getVal(fields.value?.sourceType));
const isAllSelected = computed(() => {
  const text = selectedOption.value?.text?.trim() || '';
  const value = selectedOption.value?.value?.trim() || '';
  return /^all\b/i.test(text) || /^all\b/i.test(value);
});

const parseOptions = () => {
  const rawOptions = fields.value?.options;
  if (!rawOptions) {
    parsedOptions.value = [];
    return;
  }

  if (Array.isArray(rawOptions)) {
    parsedOptions.value = rawOptions.map((opt) => ({
      value: opt.fields?.data?.value || opt.fields?.data?.value || opt.id,
      text: opt.fields?.text?.value || opt.name,
    }));
    return;
  }
};

const toggleFocus = async () => {
  if (isDisabled.value) return;
  focused.value = !focused.value;
  if (!focused.value) return;

  if (isMobileOrTablet.value) {
    await mobileModalRef.value?.open();
    await nextTick();
    mobileCurtainRef.value?.focusSearch();
  } else {
    await nextTick();
    desktopCurtainRef.value?.focusSearch();
  }
};

// const onBlur = (e: any) => {
//   console.log('blur', e);
//   if (blurTimeout.value) clearTimeout(blurTimeout.value);
//   blurTimeout.value = setTimeout(() => {
//     if (!isMobileOrTablet.value) {
//       focused.value = false;
//     }
//   }, 150);
// };

const handleOptionChange = (option: FormDropdownOption | undefined, index?: number) => {
  selectedOption.value = option;
  focused.value = false;
  if (isMobileOrTablet.value) mobileModalRef.value?.close();
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, option?.value, option);
};

const handleClear = () => {
  selectedOption.value = null;
  focused.value = false;
  if (isMobileOrTablet.value) mobileModalRef.value?.close();
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, null, null);
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue: string) => {
  if (!newValue) {
    selectedOption.value = null;
    return;
  }
  const option = parsedOptions.value.find((x) => x.value === newValue);
  selectedOption.value = option || null;
};

const updateOptions = (newOptions?: FormDropdownOption[]) => {
  parsedOptions.value = newOptions || [];
};

const validate = async () => {
  const result = {
    value: selectedOption.value?.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }
  if (isRequired.value && !selectedOption.value) {
    msg.value = {
      type: 'error',
      message: fields.value?.requiredMsg?.value || 'This field is required',
    };
    result.valid = false;
    return result;
  }
  msg.value = undefined;
  return result;
};
const onMobileModalClosed = () => {
  focused.value = false;
};
const onBodyClick = (e: any) => {
  if (isMobileOrTablet.value) return;
  if (!rootEl.value?.contains(e.target) && rootEl.value !== e.target) {
    focused.value = false;
  }
};

const getRefData = (): FormDropdownRef => ({
  el: rootEl.value,
  type: FIELD_TYPE,
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  updateOptions,
  validate,
  getValue: () => selectedOption.value?.value,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);

watch(
  () => props.item,
  () => {
    parseOptions();
    const initialValue = fields.value?.selectedOption?.fields?.data?.value;
    if (initialValue) {
      selectedOption.value = parsedOptions.value.find((o) => o.value === initialValue) || null;
    }
  },
  { immediate: true, deep: true }
);
watch(
  () => ({ isMobileOrTablet, focused }),
  () => {
    if (isMobileOrTablet.value && focused.value) {
      mobileModalRef.value?.open();
    }
  }
);
onMounted(async () => {
  formContext.regFormEl(getRefData());
  if (sourceType.value === 'country') {
    loading.value = true;
    parsedOptions.value = await services.country.getCountryOptions();
    loading.value = false;
  }
});
onMounted(() => {
  document.body.addEventListener('click', onBodyClick);
});
onBeforeUnmount(() => {
  document.body.removeEventListener('click', onBodyClick);
  formContext.unRegFormEl(getRefData());
});
defineExpose(getRefData());
</script>
<template>
  <div
    :class="[
      'f-dropdown',
      { focused: focused, disabled: isDisabled, 'has-error': msg?.type === 'error' },
    ]"
    ref="rootEl"
    v-if="isVisible"
  >
    <div class="f-dropdown__main">
      <div class="f-dropdown__selection" @click="toggleFocus" tabindex="0">
        <div
          class="f-dropdown__placeholder"
          :class="[{ 'as-label': focused || !!selectedOption }]"
          v-if="showLabel"
        >
          <span class="f-dropdown__label" v-html="label"></span>
          <span class="f-dropdown__required" v-if="isRequired">*</span>
        </div>
        <div class="f-dropdown__value">
          {{ selectedOption?.text }}
        </div>
        <Icon name="down" size="xxs" class="f-dropdown__icon" />
        <Icon
          name="close"
          size="xxs"
          class="f-dropdown__clear"
          v-if="selectedOption && !isAllSelected"
          @click.stop="handleClear"
        />
      </div>

      <div class="f-dropdown__pop" v-show="!isMobileOrTablet && focused">
        <FormDropdownCurtain
          ref="desktopCurtainRef"
          :options="parsedOptions"
          :loading="loading"
          :selectedOption="selectedOption"
          :clearable="clearable"
          :clearText="clearText"
          @optionClick="handleOptionChange"
          @clear="handleClear"
        />
      </div>
    </div>
    <form-el-msg :msg="msg" />
    <modal
      class="f-dropdown__mobile-modal"
      animation="bottom-slide-in"
      closable
      :sticky="false"
      ref="mobileModalRef"
      @closed="onMobileModalClosed"
    >
      <FormDropdownCurtain
        ref="mobileCurtainRef"
        :options="parsedOptions"
        :loading="loading"
        :selectedOption="selectedOption"
        :clearable="clearable"
        :clearText="clearText"
        @optionClick="handleOptionChange"
        @clear="handleClear"
      />
    </modal>
  </div>
</template>
<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.f-dropdown {
  $m: '.e-modal';
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  outline: none;
  color: var(--item-color, #000000);
  &__placeholder {
    position: absolute;
    width: 100%;
    height: 60px;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--item-color, #929292);
    font-size: 16px;
    padding: 10px 44px 10px 16px;
    transition: all 0.2s ease-in-out;
    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }
  &__main {
    position: relative;
    height: 60px;
    border-radius: 4px;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background-color: var(--item-back-color, #ffffff);
    &:focus-within {
      box-shadow: 0 0 0 2px rgba(8, 49, 194, 0.1);
    }
  }

  &__selection {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 100%;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }

  &__value {
    flex: 1;
    align-self: flex-end;
    font-family: 'Figtree', sans-serif;
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: $black;
  }

  &__clear {
    flex-shrink: 0;
    cursor: pointer;
    color: $grey;
    transition:
      color 0.2s ease,
      opacity 0.2s ease;
    &:hover {
      color: $black;
    }
  }

  &__icon {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &__pop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    background-color: var(--item-curtain-back-color, $white);
    color: var(--item-curtain-color, #000000);
    border: 1px solid var(--item-border-color, #c4c4c4);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  &__mobile-modal {
    z-index: 1100;

    &#{$m} {
      z-index: 1100;
      align-items: flex-end;

      #{$m}__mask {
        z-index: 0;
      }

      #{$m}__content {
        position: relative;
        z-index: 1;
        width: 100vw;
        &-detail {
          padding-top: 60px;
          max-height: 90vh;
          min-height: 300px;
        }
      }
    }
  }
  &.focused {
    .f-dropdown__selection {
      border-color: #6b9171;
      box-shadow: 0 0 0 2px rgba(8, 49, 194, 0.1);
    }

    .f-dropdown__icon {
      transform: rotate(180deg);
    }
  }

  &.disabled {
    .f-dropdown__selection {
      background-color: rgba($grey, 0.3);
      cursor: not-allowed;
    }
  }

  &.has-error {
    .f-dropdown__selection {
      border-color: #dc3545;
    }
  }

  &__error {
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
  }
}
</style>
